import { z } from 'zod';

/**
 * §9.2.3 — Depo paneli seçici profili (ADR-006).
 *
 * Seçiciler **koda gömülmez**. Depo sitesinin HTML'i değiştiğinde yeni sürüm
 * beklemek yerine kullanıcı bu profili ekrandan düzenleyip "test et" diyebilmeli.
 *
 * Her alan bir **dizi**dir: alternatifler sırayla denenir, ilk tutan kazanır.
 * Tek seçici yazmak, sitenin küçük bir değişikliğinde otomasyonu tamamen
 * durdururdu; alternatifler bu kırılganlığı azaltır.
 */

/** En az bir seçici içeren alternatif listesi. */
const selectors = z.array(z.string().trim().min(1)).min(1, 'En az bir seçici gerekli').max(10);
/** İsteğe bağlı alan — boş bırakılabilir. */
const optionalSelectors = z.array(z.string().trim().min(1)).max(10).default([]);

/**
 * Örnek profilin sürümü.
 *
 * Şartnamedeki örnek profil gerçek panellerle karşılaştıkça iyileşiyor
 * (ör. sürüm 2'de İngilizce metin alternatifleri eklendi). Kayıtlı profiller
 * kendiliğinden güncellenmez — kullanıcının düzenlemesini ezmek doğru olmaz —
 * ama arayüz eski sürümü fark edip güncelleme teklif edebilsin diye damgalanır.
 */
export const SELECTOR_PROFILE_VERSION = 6;

export const selectorProfileSchema = z.object({
  /** Bu profil hangi örnek sürümünden türedi? */
  version: z.number().int().min(1).default(1),

  /**
   * Fiyat hangi yoldan alınacak?
   *
   * · `CALCULATOR` — panelin **fiyat hesaplayıcı** sayfası. Tercih edilen yol:
   *   gönderi oluşturmaz, "Kaydet" düğmesi yoktur, ölçüleri panel kendisi çeker.
   * · `SHIPMENT_FORM` — §9.2.2'deki gönderi formu akışı. Panelde hesaplayıcı
   *   yoksa kullanılır; "Kaydet"e basmama koruması bu yolda kritiktir.
   */
  mode: z.enum(['CALCULATOR', 'SHIPMENT_FORM']).default('CALCULATOR'),

  login: z.object({
    url: z.string().trim().min(1, 'Boş bırakılamaz').default('/Account/Login'),
    email: selectors,
    password: selectors,
    submit: selectors,
    /** Giriş başarılı mı? Bu görünüyorsa evet. */
    successIndicator: selectors,
  }),

  form: z.object({
    addProductButton: selectors,
    /** Yeni eklenen satır — genelde `tbody tr:last-child`. */
    row: z.string().trim().min(1, 'Boş bırakılamaz'),
    asinInput: selectors,
    amazonFetchButton: selectors,
    /** ASIN çekimi bitti mi? Bu alan dolunca evet. */
    productNameCell: selectors,
    weightField: optionalSelectors,
    dimL: optionalSelectors,
    dimW: optionalSelectors,
    dimH: optionalSelectors,
  }),

  /**
   * Fiyat hesaplayıcı sayfası (`mode: 'CALCULATOR'`).
   *
   * Akış: sayfayı aç → varış ülkesi + posta kodu → birim lb·in → "pazar yerinden
   * ölçü getir" bölümünü aç → ASIN yaz → Amazon düğmesi → ölçüler dolar →
   * "Calculate Price" → en iyi fiyatı oku.
   *
   * Bu sayfada gönderi oluşturan bir düğme yoktur; gönderi formuna göre hem
   * daha hızlı hem risksizdir.
   */
  calculator: z
    .object({
      /** Panelin fiyat hesaplayıcı yolu. */
      path: z.string().trim().min(1, 'Boş bırakılamaz').default('/Cargo/CargoPriceCalculator'),
      destinationCountry: selectors,
      /** SELECT'te seçilecek ülke — ISO kodu veya tam ad. */
      destinationCountryValue: z.string().trim().default('Japan'),
      /**
       * Özel açılır liste (Select2, Choices.js…) kapsayıcısı.
       *
       * Bu bileşenler gerçek `<select>`i gizler ve yerine kendi arayüzünü koyar;
       * gizli select'e yazmak panelin kendi olaylarını tetiklemez. Doluysa
       * otomasyon kullanıcı gibi davranır: kutuya tıkla → ara → seçeneğe tıkla.
       */
      dropdownContainer: optionalSelectors,
      /** Açılır listenin arama kutusu. */
      dropdownSearch: optionalSelectors,
      /** Açılır listedeki seçenekler. */
      dropdownOption: optionalSelectors,
      postalCode: selectors,
      /** lb·in birimini seçen düğme; panel kg·cm açılırsa fiyat yanlış olur. */
      unitImperialToggle: optionalSelectors,
      /** "Get weight and dimensions from marketplace" açılır bölümü. */
      marketplaceSection: optionalSelectors,
      asinInput: selectors,
      quantityInput: optionalSelectors,
      /** Ölçüleri Amazon'dan çeken düğme. */
      fetchButton: selectors,
      /** Ölçünün dolduğunu anlamak için izlenen alan. */
      weightField: selectors,
      lengthField: optionalSelectors,
      widthField: optionalSelectors,
      heightField: optionalSelectors,
      calculateButton: selectors,
      /** Sonuç kartları; "en iyi fiyat" bunlardan biri. */
      resultRow: selectors,
      resultCarrier: selectors,
      resultService: optionalSelectors,
      resultPrice: selectors,
      resultTransit: optionalSelectors,
      /** ★ Best Price rozetli kart — varsa doğrudan o alınır. */
      bestPriceRow: optionalSelectors,
    })
    .optional(),

  /**
   * Paket ölçüleri (§9.2.2 ADIM 2 varyantı).
   *
   * Şartname "amazon butonuna bas, ağırlık/boyut otomatik dolsun" diyor; bazı
   * paneller (ör. ShipTroy) böyle çalışmıyor ve ölçüleri **elle girmenizi**
   * bekliyor. Bu alanlar doluysa otomasyon kendi ölçü verisini forma yazar.
   *
   * ⚠️ Ölçü yazılamıyorsa fiyat sorulmaz: panelin varsayılanı (1×1×1, 1 lb)
   * ile alınan teklif gerçekte olmayan bir kutuya aittir ve o yanlış fiyat
   * ürünün maliyetine kalıcı olarak yazılırdı.
   */
  package: z
    .object({
      weight: optionalSelectors,
      /** kg / lb / oz seçimi. */
      weightUnit: optionalSelectors,
      weightUnitValue: z.string().trim().default('lb'),
      length: optionalSelectors,
      width: optionalSelectors,
      height: optionalSelectors,
      /** cm / in seçimi. */
      dimensionUnit: optionalSelectors,
      dimensionUnitValue: z.string().trim().default('in'),
      /** "İçerik" alanı — bazı paneller zorunlu tutuyor. */
      contents: optionalSelectors,
      contentsValue: z.string().trim().default('Merchandise'),
    })
    .default({
      weight: [],
      weightUnit: [],
      weightUnitValue: 'lb',
      length: [],
      width: [],
      height: [],
      dimensionUnit: [],
      dimensionUnitValue: 'in',
      contents: [],
      contentsValue: 'Merchandise',
    }),

  recipient: z.object({
    fullName: selectors,
    country: selectors,
    /** SELECT'te seçilecek option değeri — yazılmaz, seçilir (§9.2.2 ADIM 3). */
    countryValue: z.string().trim().min(1).default('JP'),
    address1: selectors,
    address2: optionalSelectors,
    city: optionalSelectors,
    postcode: selectors,
    state: optionalSelectors,
    /** "CA, NY" gibi kısa eyalet kodu isteyen paneller için. */
    stateCode: optionalSelectors,
    /** Bazı paneller kapı numarasını ayrı istiyor. */
    houseNo: optionalSelectors,
    phone: optionalSelectors,
    email: optionalSelectors,
  }),

  quote: z.object({
    getRatesButton: selectors,
    modal: selectors,
    rateRow: selectors,
    rateCarrier: selectors,
    rateService: optionalSelectors,
    ratePrice: selectors,
    rateTransitDays: optionalSelectors,
    closeButton: selectors,
  }),

  /**
   * ⛔ §9.2.2 ADIM 5 — "Kaydet" butonuna ASLA basılmaz.
   *
   * Bu seçici basmak için değil, **kaçınmak** için tutulur: otomasyon tıklamadan
   * önce hedefin bu olmadığını doğrular. Depo panelinde "Kaydet" gerçek bir
   * gönderi oluşturur ve kullanıcıya para/işlem yükü çıkarır.
   */
  forbidden: z
    .object({
      saveButton: optionalSelectors,
      /**
       * ⛔ §EK-C — "İlave Hizmetler: HEPSİ KAPALI. Fiyatı şişirmesin."
       *
       * Uluslararası transit, adaptör, kutu, bubble wrap gibi ek hizmetler
       * fiyata eklenir. Panelde bazıları varsayılan işaretli gelebilir; o hâlde
       * aldığımız teklif gerçek kargo maliyetinden pahalı olur ve **ürün
       * fiyatını gereksiz yükseltir** — rekabet gücü kaybı, fark edilmesi zor.
       * Bu seçiciler işaretliyse **kaldırılır**.
       */
      extraServiceCheckboxes: optionalSelectors,
    })
    .default({ saveButton: [], extraServiceCheckboxes: [] }),

  timeouts: z
    .object({
      asinFetchMs: z
        .number()
        .int()
        .min(1_000, 'En az 1 saniye olmalı')
        .max(120_000, 'En fazla 120 saniye olabilir')
        .default(25_000),
      rateFetchMs: z
        .number()
        .int()
        .min(1_000, 'En az 1 saniye olmalı')
        .max(180_000, 'En fazla 180 saniye olabilir')
        .default(40_000),
      navigationMs: z
        .number()
        .int()
        .min(1_000, 'En az 1 saniye olmalı')
        .max(120_000, 'En fazla 120 saniye olabilir')
        .default(30_000),
    })
    .default({ asinFetchMs: 25_000, rateFetchMs: 40_000, navigationMs: 30_000 }),
});

export type SelectorProfile = z.infer<typeof selectorProfileSchema>;
export type SelectorProfileInput = z.input<typeof selectorProfileSchema>;

/**
 * §9.2.3'teki örnek profil — yeni depo eklendiğinde başlangıç noktası.
 *
 * Bu değerler **tahmindir**; kullanıcı "test et" ile doğrulayana kadar
 * çalışacağı varsayılmaz.
 */
export const DEFAULT_SELECTOR_PROFILE: SelectorProfileInput = {
  version: SELECTOR_PROFILE_VERSION,
  mode: 'CALCULATOR',
  // ShipTroy panelinden okunan gerçek alanlara göre yazıldı
  calculator: {
    path: '/Cargo/CargoPriceCalculator',
    destinationCountry: [
      "select[name*='Country' i]",
      "select:near(:text('To'))",
      'select',
    ],
    destinationCountryValue: 'Japan',
    // ShipTroy ülke alanında Select2 kullanıyor: gerçek select gizli,
    // görünen şey `#select2-CountryTo-container` span'i
    dropdownContainer: [
      '#select2-CountryTo-container',
      "[id^='select2-'][id*='Country'][id$='-container']",
      '.select2-selection',
      "[class*='select2-selection']",
    ],
    dropdownSearch: ['.select2-search__field', "input[class*='search__field']"],
    dropdownOption: [
      '.select2-results__option',
      "li[class*='results__option']",
      "[role='option']",
    ],
    postalCode: [
      "input[name*='Postal' i]",
      "input[name*='Zip' i]",
      "input[placeholder*='Postal' i]",
    ],
    unitImperialToggle: [
      "button:has-text('lb · in')",
      "button:has-text('lb·in')",
      "label:has-text('lb · in')",
      "text=lb · in",
    ],
    marketplaceSection: [
      "button:has-text('Get weight and dimensions from marketplace')",
      "text=Get weight and dimensions from marketplace",
      "button:has-text('Pazar yerinden')",
    ],
    asinInput: [
      "input[name*='asin' i]",
      "input[placeholder*='ASIN' i]",
      "input[name*='ItemId' i]",
      "input[name*='ListingId' i]",
    ],
    quantityInput: ["input[name*='quantity' i]", "input[placeholder*='Quantity' i]"],
    fetchButton: [
      "button:has-text('Amazon')",
      "a:has-text('Amazon')",
      "button:has(img[alt*='amazon' i])",
    ],
    weightField: ["input[name*='weight' i]", "input[name='Weight']"],
    lengthField: ["input[name*='length' i]"],
    widthField: ["input[name*='width' i]"],
    heightField: ["input[name*='height' i]"],
    calculateButton: [
      "button:has-text('Calculate Price')",
      "button:has-text('Fiyat Hesapla')",
      "button:has-text('Calculate')",
    ],
    // Tek elementte birden çok sınıf olabilir (`.result-card`), ata-çocuk da
    // olabilir; ikisi de denenir
    resultRow: [
      "[class*='result' i][class*='card' i]",
      "[class*='rate' i][class*='row' i]",
      "[class*='result' i] [class*='card' i]",
      "[class*='rate' i]",
      'table tbody tr',
    ],
    resultCarrier: ["[class*='carrier' i]", 'strong', 'td:nth-child(1)'],
    resultService: ["[class*='service' i]", 'td:nth-child(2)'],
    resultPrice: ["[class*='price' i]", 'td:last-child'],
    resultTransit: ["[class*='transit' i]", "[class*='days' i]"],
    bestPriceRow: ["[class*='best' i]", ":has-text('Best Price')"],
  },
  login: {
    url: '/Login',
    email: ["input[name='Email']", '#Email', "input[type='email']", "input[name*='mail' i]"],
    password: ["input[name='Password']", '#Password', "input[type='password']"],
    submit: ["button[type='submit']", "input[type='submit']", 'text=Giriş', 'text=Login', 'text=Sign in'],
    successIndicator: ['text=Dashboard', 'text=Anasayfa', 'text=Shipments', 'text=Gönderiler'],
  },
  form: {
    // Panel dili kullanıcıya göre değişir; her iki dil de denenir
    addProductButton: [
      "button:has-text('Add New Product')",
      "button:has-text('Yeni Ürün Ekle')",
      "a:has-text('Add New Product')",
      "a:has-text('Yeni Ürün')",
      '.btn-add-product',
    ],
    row: 'table tbody tr:last-child',
    asinInput: ["input[placeholder*='ASIN' i]", "input[name*='asin' i]", 'td:nth-child(3) input'],
    amazonFetchButton: [
      "button:has(img[alt*='amazon' i])",
      "img[alt*='amazon' i]",
      "button:has-text('Get')",
      "button:has-text('Getir')",
      '.btn-amazon',
      "[title*='Amazon' i]",
    ],
    productNameCell: [
      "input[name*='ProductName' i]",
      "input[placeholder*='Product Name' i]",
      "input[placeholder*='Ürün Adı' i]",
      'td:nth-child(2) input',
    ],
    weightField: ["input[name*='weight' i]", "input[name*='agirlik' i]", "input[name*='ağırlık' i]"],
    dimL: ["input[name*='length' i]", "input[name*='uzunluk' i]", "input[name*='boy' i]"],
    dimW: ["input[name*='width' i]", "input[name*='genislik' i]", "input[name*='en' i]"],
    dimH: ["input[name*='height' i]", "input[name*='yukseklik' i]", "input[name*='yükseklik' i]"],
  },
  // Paket ölçüleri gönderi düzeyinde girilir (ShipTroy'da doğrulandı)
  package: {
    weight: ["input[name='Weight']", "input[name*='weight' i]"],
    weightUnit: ["select[name='WeightUnit']", "select[name*='weightunit' i]"],
    weightUnitValue: 'lb',
    length: ["input[name='Length']", "input[name*='length' i]"],
    width: ["input[name='Width']", "input[name*='width' i]"],
    height: ["input[name='Height']", "input[name*='height' i]"],
    dimensionUnit: ["select[name='DimensionUnit']", "select[name*='dimensionunit' i]"],
    dimensionUnitValue: 'in',
    contents: ["input[name='Contents']", "input[name*='contents' i]"],
    contentsValue: 'Merchandise',
  },
  recipient: {
    fullName: [
      "input[name='Buyer.Name']",
      "input[placeholder*='Receiver Name' i]",
      "input[name*='FullName' i]",
      "input[placeholder*='Ad Soyad' i]",
    ],
    country: ["select[name='Buyer.CountryId']", "select[name*='Country' i]", "select[name*='Ülke' i]"],
    // Bazı paneller ülkeyi ISO koduyla, bazıları tam adıyla tutar — ikisi de denenir
    countryValue: 'Japan',
    address1: [
      "input[name='Buyer.Street1']",
      "input[placeholder*='Address Line 1' i]",
      "input[name*='Address1' i]",
      "input[placeholder*='Adres Satırı 1' i]",
    ],
    address2: ["input[name='Buyer.Street2']", "input[placeholder*='Address Line 2' i]"],
    city: ["input[name='Buyer.City']", "input[placeholder*='City' i]", "input[name*='Sehir' i]"],
    postcode: [
      "input[name='Buyer.ZipCode']",
      "input[placeholder*='Postal Code' i]",
      "input[name*='Zip' i]",
      "input[placeholder*='Posta' i]",
    ],
    state: ["input[name='Buyer.State']", "input[placeholder*='State' i]", "input[name*='Eyalet' i]"],
    stateCode: ["input[name='Buyer.StateCode']"],
    houseNo: ["input[name='Buyer.HouseNo']", "input[placeholder*='House No' i]"],
    phone: ["input[name='Buyer.Phone']", "input[type='tel']", "input[name*='Telefon' i]"],
    email: ["input[name='Buyer.Email']", "input[name*='Email' i]"],
  },
  quote: {
    // ⚠️ "Save" düğmesinin hemen SOLUNDA — yanlış tıklama gerçek gönderi oluşturur
    getRatesButton: [
      "button:has-text('Shipping Prices')",
      "button:has-text('Kargo Fiyatları')",
      "button:has-text('Shipping Rates')",
      "button:has-text('Get Rates')",
      '.btn-rates',
    ],
    modal: [
      ".modal:has-text('Shipping')",
      ".modal:has-text('Kargo')",
      "[role='dialog']",
      '.modal.show',
    ],
    rateRow: ['.modal .rate-row', "[role='dialog'] tbody tr", '.modal tbody tr', "[role='dialog'] li"],
    rateCarrier: ['.carrier-name', 'td:nth-child(1)'],
    rateService: ['.service-name', 'td:nth-child(2)'],
    ratePrice: ['.price', 'td:last-child'],
    rateTransitDays: ['.transit-days'],
    closeButton: [
      '.modal .close',
      "[aria-label='Close']",
      "button:has-text('Close')",
      "button:has-text('Kapat')",
    ],
  },
  forbidden: {
    // ⛔ §EK-C — ilave hizmetler fiyatı şişirir; işaretliyse kaldırılır
    extraServiceCheckboxes: [
      "input[type='checkbox'][name*='ExtraService' i]",
      "input[type='checkbox'][name*='AdditionalService' i]",
      "input[type='checkbox'][name*='IlaveHizmet' i]",
    ],
    // ⛔ Bu düğmelere ASLA basılmaz; liste kaçınmak için tutulur (§9.2.2 ADIM 5)
    saveButton: [
      "button:has-text('Kaydet')",
      "button:has-text('Save')",
      "button:has-text('Create Shipment')",
      "button:has-text('Submit')",
      '.btn-save',
      "input[type='submit'][value='Kaydet']",
      "input[type='submit'][value='Save']",
    ],
  },
  timeouts: { asinFetchMs: 25_000, rateFetchMs: 40_000, navigationMs: 30_000 },
};

/** §17.1 — Kargo sorgusu hata tipleri; kullanıcıya Türkçe gösterilir. */
export const CARGO_FAILURE_LABELS: Record<string, string> = {
  SELECTOR_MISMATCH: 'Depo panelinin arayüzü değişmiş olabilir',
  ASIN_NOT_FOUND: 'Depo bu ASIN için ürün bulamadı',
  AUTH_FAILED: 'Depo paneline giriş yapılamadı',
  TIMEOUT: 'Depo paneli zamanında yanıt vermedi',
  NETWORK: 'Depo paneline ulaşılamadı',
  NO_RATES: 'Hiç kargo teklifi dönmedi',
  RATE_LIMITED: 'Depo paneli hız sınırı uyguladı',
  UNKNOWN: 'Beklenmeyen hata',
};

/** Hatalar sekmesi: her hata tipinde kullanıcının yapacağı iş. */
export const CARGO_FAILURE_ACTIONS: Record<string, string> = {
  SELECTOR_MISMATCH:
    "Ayarlar sekmesinden bir ASIN ile test edin. Test geçmezse Seçici Profili'ni gözden geçirin.",
  ASIN_NOT_FOUND:
    'Panel bu ürünün ölçülerini getiremedi. Kargo fiyatını Excel sekmesinden girebilirsiniz.',
  AUTH_FAILED: 'Ayarlar sekmesinden depo e-posta ve şifresini kontrol edip test edin.',
  TIMEOUT:
    "Bir süre sonra çekimi yeniden başlatın. Sürerse Seçici Profili'ndeki bekleme sürelerini artırın.",
  NETWORK: 'Panel adresini kontrol edip çekimi yeniden başlatın.',
  NO_RATES: 'Alıcı adresindeki ülke ve posta kodunu kontrol edin.',
  RATE_LIMITED: 'Ayarlar sekmesinde "Sorgu / dakika" değerini düşürüp çekimi yeniden başlatın.',
  UNKNOWN: 'Çekimi yeniden başlatın. Sürerse teknik ayrıntıyı destek ile paylaşın.',
};
